/* This example requires Tailwind CSS v2.0+ */
import React, {
  Fragment,
  cloneElement,
  createContext,
  useContext,
  useState,
} from "react";
import { usePopper } from "react-popper";

import cn from "@/helpers/classNames";
import { Menu, Portal, Transition } from "@headlessui/react";
import { ComponentProps } from "@tw-classed/react";

import { EllipsisIcon } from "lucide-react";
import { IconButton } from "../elements/Button";

const TableMenuContext = createContext<{
  size?: "sm";
  ref: React.RefCallback<HTMLButtonElement>;
} | null>(null);

const useTableMenuContext = () => {
  const context = useContext(TableMenuContext);
  if (!context) {
    throw new Error("useTableMenuContext must be used within a TableMenu");
  }
  return context;
};

export default function TableMenu({
  children,
  size,
  button: buttonComponent,
  menuItemsClassName,
}: React.PropsWithChildren<{
  size?: "sm";
  button?: React.ReactNode;
  menuItemsClassName?: string;
}>) {
  const [referenceElement, setReferenceElement] = useState<any>();
  const [popperElement, setPopperElement] = useState<any>();
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    strategy: "absolute",
    placement: "bottom-end",
    modifiers: [
      { name: "preventOverflow", enabled: false },
      { name: "offset", options: { offset: [0, 8] } },
    ],
  });

  return (
    <TableMenuContext.Provider value={{ size, ref: setReferenceElement }}>
      <Menu as="div" className="flex shrink-0 text-left">
        {({ open }) => (
          <>
            {buttonComponent ? (
              <div className="inline-flex" ref={setReferenceElement}>
                {buttonComponent}
              </div>
            ) : (
              <MenuButton />
            )}

            <Portal>
              <Transition
                as={Fragment}
                show={open}
                enter="transition-opacity ease-out duration-100"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="transition-opacity ease-in duration-75"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Menu.Items
                  ref={setPopperElement}
                  style={styles.popper}
                  {...attributes.popper}
                  static
                  className={cn(
                    "z-50 w-56 divide-y rounded-sm border bg-background shadow-lg outline-hidden focus:outline-hidden dark:bg-gray-900",
                    menuItemsClassName,
                  )}
                  onClick={(e) => e.stopPropagation()}
                >
                  {children}
                </Menu.Items>
              </Transition>
            </Portal>
          </>
        )}
      </Menu>
    </TableMenuContext.Provider>
  );
}

export function MenuButton(props: Partial<ComponentProps<typeof IconButton>>) {
  const { size, ref } = useTableMenuContext();
  const IconComponent = props.icon ? props.icon : <EllipsisIcon />;
  return (
    <Menu.Button
      ref={ref as any}
      onClick={(e: any) => e.stopPropagation()}
      {...props}
      disabled={Boolean(props.disabled)}
      as={IconButton}
      variant="ghost"
      size="sm"
      icon={cloneElement(IconComponent as any, {
        className: size === "sm" ? "size-3" : "size-4",
        "aria-hidden": true,
      })}
      className={cn(
        "size-7 min-w-7 border hover:border-gray-300 dark:hover:border-gray-600",
        {
          "size-5 min-w-5": size === "sm",
        },
        props.className,
      )}
    >
      <span className="sr-only">Open options</span>
    </Menu.Button>
  );
}
